import { FeParamsPage } from '@porterin/component-library/src/ts-utils/analytics/configs/FeParamsPage';
import { getAppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/FetchConfig';
import { saveToStoreAndRedirect } from '@porterin/component-library/src/ts-utils/analytics/helpers/FeParamsHelper';
import {
  publishEventParameter,
  publishMoengageEventParameter,
  publishEvents,
} from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import {
  DefaultValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';

import { getTypeFromStringV2 } from '../domain/configs/CategoryTypes';
import { defaultRequirement } from '../domain/configs/Config';
import {
  ga_event_2w,
  ga_fare_estimate_form_validation,
  service_category_trucks,
  service_category_two_wheelers,
  ga_fare_estimate_successful,
  ga_fare_estimate_successful_2w,
  ga_fare_estimate_successful_trucks,
  ga_fare_estimate_successful_partload,
  FareEstimateFormLabel,
  success_v2_events_uae,
  form_submit_ga_event_uae,
  form_submit_ga_event_turkey,
  form_success_ga_event_turkey,
} from '../domain/configs/Store.store';
import { AuthBinding } from '../domain/entities/AuthBinding';
import {
  FareEstimateForm,
  FormType,
} from '../domain/entities/FareEstimateForm';
import {
  CallbackResponse,
  FareEstimateValidator,
} from '../domain/entities/FareEstimateValidator';
import { FareEstimateFieldErrors } from '../domain/entities/FareEstimateValidatorError';
import {
  fireGTEEventsOnLeadCreated,
  submitButtonGaEvent,
} from '../domain/helpers/FareEstimateEventHelper';
import { submitFareEstimateSecure } from '../domain/repo/FareEstimateSecureRepo';
import { submitFareEstimate } from '../domain/repo/FareEstimateValidatorRepo';
import {
  moe_fe_est_result_cta_confirm,
  trucks_wheeler_source,
  two_wheeler_source,
  vehicle_2w_source,
  vehicle_trucks_source,
} from '../domain/store/FareEstimateStore.store';
import { PerLanguageConfig } from '@porterin/component-library/src/domain/country/type';
import {
  getUtmParams,
  parseUtmParams,
  withRedirectUtmDefaults,
} from './FareEstimateUtmParamsHelper';
import { FormData } from './FareEstimateValidationSchema';
import { submitPartloadLead } from './PartloadLeadHelper';
import Router from 'next/router';
import { getLocaleCookie } from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';
const Config = getAppConfig();

export function getCategoryPayload(service_category: FormType): string {
  switch (service_category) {
    case FormType.TwoWheeler:
      return FareEstimateFormLabel.two_wheelers_label_payload;
    case FormType.Partload:
      return FareEstimateFormLabel.partload_label_payload;
    case FormType.Trucks:
    default:
      return FareEstimateFormLabel.trucks_label_payload;
  }
}

function convertToFareEstimateForm(
  formData: FormData,
  service_category: FormType = FormType.TwoWheeler,
  geo_region_id?: string,
): FareEstimateForm {
  return {
    name: formData.name,
    mobile: formData.mobile,
    requirement: formData.requirement,
    pickupAddress: {
      landmark: formData.pickup.landmark,
      location: formData.pickup.location,
    },
    dropAddress: {
      landmark: formData.drop.landmark,
      location: formData.drop.location,
    },
    serviceCategory: getCategoryPayload(service_category),
    geoRegionId: geo_region_id,
  };
}

export const updateErrorMessages = (
  newMessages: FareEstimateFieldErrors,
  setError: UseFormSetError<FormData>,
): void => {
  Object.keys(newMessages).forEach(key => {
    const fieldKey = key as keyof FareEstimateFieldErrors;
    const message = newMessages[fieldKey];
    if (message) {
      setError(fieldKey, {
        type: 'manual',
        message: message,
      });
    }
  });
};

export const stringToFormType = (value: string): FormType => {
  switch (value.trim().toLowerCase()) {
    case '2w':
      return FormType.TwoWheeler;
    case 'partload':
      return FormType.Partload;
    default:
      return FormType.Trucks;
  }
};

export const replaceWithBookingFlowUrl = (
  url: string,
  is_bkf_enabled: boolean,
): string => {
  const urlObj = new URL(url);

  if (is_bkf_enabled) {
    urlObj.pathname = urlObj.pathname.replace(
      'fare_estimate_mob',
      'fare-estimate',
    );
  }

  return getLocaleCookie() + urlObj.pathname + urlObj.search + urlObj.hash;
};

const getCallbackReqKeyValuePairs = (
  data: CallbackResponse,
): [string, string][] => [
  ['result', String(data.result)],
  ['prev_callback_req_id_present', String(data.prevCallbackReqIdPresent)],
  ['callback_request_id', data.callbackRequestId],
  ['is_existing_cust', String(data.isExistingCust)],
  ['lead_entry_created', String(data.leadEntryCreated ?? false)],
];

export const makeCallbackDataQueryParams = (
  callbackRequestData: CallbackResponse,
): string => {
  return getCallbackReqKeyValuePairs(callbackRequestData).reduce(
    (paramUrl, [key, value]) => {
      return paramUrl + `&${key}=${encodeURIComponent(value)}`;
    },
    '',
  );
};

export const isDesktop = (): boolean => {
  if (typeof window !== 'undefined') {
    return typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 560px)').matches
      : false;
  }
  return false;
};

export function makeOrderReviewLink(
  service_category: FormType,
  form_data: FareEstimateForm,
): string {
  const selectedVehicle =
    service_category === FormType.TwoWheeler ? Config.selected_2w_id || '' : '';

  return `/order-review?sender_mobile=${form_data.mobile}&sender_name=${form_data.name}&receiver_mobile=${form_data.mobile}&receiver_name=${form_data.name}&from_landmark=${form_data.pickupAddress.landmark}&from_lat=${form_data.pickupAddress.location.lat}&from_long=${form_data.pickupAddress.location.lng}&from_house=&to_landmark=${form_data.dropAddress.landmark}&to_lat=${form_data.dropAddress.location.lat}&to_long=${form_data.dropAddress.location.lng}&to_house=&vehicle_id=${selectedVehicle}`;
}

export const constructRedirectUrl = (
  fare_estimate_response: FareEstimateValidator,
  service_category: FormType,
  token: string,
  is_authenticated: boolean,
  form_data: FareEstimateForm,
  is_bkf_enabled: boolean,
  consent_for_callback: boolean = true,
): string => {
  const serviceCategoryParam = getCategoryPayload(service_category);
  if (is_authenticated) {
    return makeOrderReviewLink(service_category, form_data);
  }

  const callbackParams = makeCallbackDataQueryParams(
    fare_estimate_response.callbackRequestData,
  );
  const isDesktopParam = isDesktop();
  const utmParams = withRedirectUtmDefaults(getUtmParams());
  const redirectUrl = replaceWithBookingFlowUrl(
    fare_estimate_response.redirectUrl,
    is_bkf_enabled,
  );
  return `${redirectUrl}&service_category=${serviceCategoryParam}&is_device_mobile=${!isDesktopParam}&landing_page=nw&${callbackParams}&${utmParams}&fare_estimate_token=${token}&consent_for_callback=${consent_for_callback ? 'false' : 'true'}`;
};

export const getCityFromStorage = (default_city?: string) => {
  return window?.localStorage.getItem('user_city') ?? default_city;
};

export function publishClickAnalytics(
  locale: string,
  form_data: FormData,
  utmObject: Record<string, string>,
  category: string,
  service_category: FormType,
): void {
  if (locale === 'en-AE') {
    publishEventParameter(form_submit_ga_event_uae, {
      event_category: category,
      event_label: `1_${form_data.name}#2_${form_data.mobile}#3_frequency-${form_data.requirement}#4_${utmObject['utm_source']}#5_${utmObject['utm_campaign']}`,
      language_selected: 'English',
    });
  } else {
    publishEvents(
      category,
      submitButtonGaEvent(category, getTypeFromStringV2(service_category)),
      '',
    );

    if (locale === 'en-TR' || locale === 'tr-TR') {
      publishEventParameter(form_submit_ga_event_turkey, {
        event_category: category,
        event_label: `1_${form_data.name}#2_${form_data.mobile}#3_frequency-${form_data.requirement}#4_${utmObject['utm_source']}#5_${utmObject['utm_campaign']}`,
        language_selected: locale === 'tr-TR' ? 'Turkish' : 'English',
      });

      publishMoengageEventParameter(
        moe_fe_est_result_cta_confirm,
        {
          vehicle_type: service_category,
          name: form_data.name,
          phone_number: form_data.mobile,
        },
        locale,
      );
    }
  }
}
// Helper function to publish success analytics events
export function publishSuccessAnalytics(
  locale: string,
  form_data: FormData,
  utmObject: Record<string, string>,
  category: string,
  service_category: FormType,
): void {
  let successEventName = ga_fare_estimate_successful;
  if (service_category === FormType.Trucks) {
    successEventName = ga_fare_estimate_successful_trucks;
  } else if (service_category === FormType.TwoWheeler) {
    // Parity: .res non-BKF (TR) form mislabels 2w success as trucks; match it.
    successEventName =
      locale === 'en-TR' || locale === 'tr-TR'
        ? ga_fare_estimate_successful_trucks
        : ga_fare_estimate_successful_2w;
  } else if (service_category === FormType.Partload) {
    successEventName = ga_fare_estimate_successful_partload;
  }

  // Parity with .res: only the non-BKF form (TR/BD) double-fires success;
  // BKF locales (en-AE/en-IN) use the shared single-fire form.
  if (locale !== 'en-AE' && locale !== 'en-IN') {
    publishEvents(
      category,
      successEventName,
      `${category}_${getCategoryPayload(service_category)}_${form_data.mobile}_`,
    );
  }

  publishEvents(
    category,
    successEventName,
    `1_${form_data.name}#2_${form_data.mobile}#3_frequency-${form_data.requirement}#4_${utmObject['utm_source']}#5_${utmObject['utm_campaign']}#6_${service_category}`,
  );

  if (locale === 'en-AE') {
    const eventPayload = {
      name: form_data.name,
      mobile_number: form_data.mobile,
      city: getCityFromStorage('Dubai'),
      frequency: form_data.requirement === 'personal' ? 'Personal' : 'Business',
      language_selected: 'English',
    };

    success_v2_events_uae.forEach(eventName =>
      publishEventParameter(eventName, eventPayload),
    );
  } else if (locale === 'en-TR' || locale === 'tr-TR') {
    publishEventParameter(form_success_ga_event_turkey, {
      name: form_data.name,
      mobile_number: form_data.mobile,
      city: getCityFromStorage(''),
      frequency: form_data.requirement === 'personal' ? 'Personal' : 'Business',
      language_selected: locale === 'tr-TR' ? 'Turkish' : 'English',
    });
  }
}

// Helper function to handle lead creation analytics
export function handleLeadAnalytics(
  result: FareEstimateValidator,
  service_category: FormType,
  category: string,
): void {
  if (!result.callbackRequestData.leadEntryCreated) return;

  const serviceLabel =
    service_category === FormType.Trucks
      ? service_category_trucks
      : service_category_two_wheelers;

  let sourceLabel: string;
  if (service_category === FormType.Trucks) {
    sourceLabel = category.includes('home')
      ? trucks_wheeler_source
      : vehicle_trucks_source;
  } else {
    sourceLabel = category.includes('home')
      ? two_wheeler_source
      : vehicle_2w_source;
  }

  fireGTEEventsOnLeadCreated(
    result.redirectUrl,
    serviceLabel,
    category,
    sourceLabel,
    result.callbackRequestData.sendUnregisteredEvent ?? false,
  );
}

// Parameter interface to reduce function complexity
interface SuccessResponseParams {
  result: FareEstimateValidator;
  formData: FareEstimateForm;
  service_category: FormType;
  is_authenticated: boolean;
  is_bkf_enabled: boolean;
  form_data: FormData;
  utmObject: Record<string, string>;
  category: string;
  locale: string;
}

// Helper function to handle successful token response
export async function handleSuccessfulResponse(
  params: SuccessResponseParams,
): Promise<void> {
  const {
    result,
    formData,
    service_category,
    is_authenticated,
    is_bkf_enabled,
    form_data,
    utmObject,
    category,
    locale,
  } = params;

  const geoRegionId =
    new URL(result.redirectUrl).searchParams.get('geo_region_id') ?? undefined;
  const updatedFormData = { ...formData, geoRegionId };
  const token = await submitFareEstimateSecure(updatedFormData);

  if (!('token' in token) || typeof token.token !== 'string') {
    const error =
      'error' in token
        ? (token.error ?? 'Error in retrieving secure token')
        : 'Unexpected error occurred';
    throw new Error(error);
  }

  const finalRedirectUrl = constructRedirectUrl(
    result,
    service_category,
    token.token,
    is_authenticated,
    formData,
    is_bkf_enabled,
    form_data.consentForCallback ?? false,
  );

  publishSuccessAnalytics(
    locale,
    form_data,
    utmObject,
    category,
    service_category,
  );
  handleLeadAnalytics(result, service_category, category);

  saveToStoreAndRedirect(
    finalRedirectUrl,
    is_authenticated
      ? FeParamsPage.OrderReview
      : FeParamsPage.FareEstimateResults,
  );
}

// Helper function to handle errors
export function handleFareEstimateErrors(
  result: any,
  setError: UseFormSetError<FormData>,
  callback: (error: string) => void,
): void {
  updateErrorMessages(result.fieldErrors ?? {}, setError);
  callback(result.formError ?? '');
}

async function submitFareEstimateFormData(
  form_data: FormData,
  setError: UseFormSetError<FormData>,
  callback: (error: string) => void,
  service_category: FormType = FormType.TwoWheeler,
  category: string = 'Home',
  is_authenticated: boolean = false,
  is_bkf_enabled: boolean = false,
  on_lead_success_handler: (show: boolean) => void,
): Promise<void> {
  if (service_category === FormType.Partload) {
    return submitPartloadLead(
      form_data,
      setError,
      callback,
      category,
      on_lead_success_handler,
    );
  }

  const locale = getLocaleCookie();
  const utmParams = getUtmParams();
  const utmObject = parseUtmParams(utmParams);
  // Publish click analytics
  publishClickAnalytics(
    locale,
    form_data,
    utmObject,
    category,
    service_category,
  );

  // Convert and validate form data
  const formData = convertToFareEstimateForm(form_data, service_category);

  const serviceCategoryEvent =
    service_category === FormType.Trucks
      ? service_category_trucks
      : ga_event_2w;

  publishEvents(
    category,
    ga_fare_estimate_form_validation,
    `${serviceCategoryEvent}_${formData.mobile}`,
  );

  // Submit fare estimate
  const result = await submitFareEstimate(formData);

  try {
    if ('redirectUrl' in result) {
      await handleSuccessfulResponse({
        result,
        formData,
        service_category,
        is_authenticated,
        is_bkf_enabled,
        form_data,
        utmObject,
        category,
        locale,
      });
    } else {
      handleFareEstimateErrors(result, setError, callback);
    }
  } catch (error) {
    callback(
      error instanceof Error ? error.message : 'Unexpected error occurred',
    );
  }
}

export const isValidRequirement = (req?: 'personal' | 'business'): boolean => {
  return req === 'personal' || req === 'business';
};

export const prefillAuthData = (
  set_form_value: UseFormSetValue<FormData>,
  auth_data?: AuthBinding,
): void => {
  if (auth_data?.isAuthenticated) {
    if (auth_data?.user?.mobile) {
      set_form_value('mobile', auth_data?.user?.mobile);
    }
    if (auth_data?.user?.name) {
      set_form_value('name', auth_data?.user?.name);
    }
    if (
      isValidRequirement(
        auth_data?.user?.requirementType as 'personal' | 'business',
      )
    ) {
      set_form_value(
        'requirement',
        auth_data?.user?.requirementType as 'personal' | 'business',
      );
    } else if (auth_data?.user) {
      set_form_value('requirement', defaultRequirement);
    }
    if (auth_data.user?.whatsappConsent !== undefined) {
      set_form_value('consentForCallback', auth_data.user?.whatsappConsent);
    }
  }
};

export const getInitialValues = (
  auth_data?: AuthBinding,
): DefaultValues<FormData> => ({
  mobile: auth_data?.user?.mobile,
  name: auth_data?.user?.name,
  requirement: isValidRequirement(
    auth_data?.user?.requirementType as 'personal' | 'business',
  )
    ? (auth_data?.user?.requirementType as 'personal' | 'business')
    : auth_data?.user
      ? defaultRequirement
      : undefined,
  consentForCallback: auth_data?.user?.whatsappConsent ?? true,
});

export { submitFareEstimateFormData, convertToFareEstimateForm };

export const getScreenName = (): string =>
  [
    { pattern: '/fare-estimate', name: 'Fare Estimate' },
    { pattern: '/order-review', name: 'Order Review' },
  ].find(({ pattern }) => Router?.pathname?.includes(pattern))?.name ?? '';

export const getIsBookingFlowEnabled = (configs: PerLanguageConfig) => {
  return isDesktop()
    ? configs.bookingFlowConfig?.isDesktopBkfEnabled
    : configs.bookingFlowConfig?.isMobileBkfEnabled;
};
